import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Copy, Check, Download, Sparkles, Wand2, Lock, Zap, Crown } from "lucide-react";
import { toast } from "sonner";
import { generateSunoPrompt } from "../lib/generatePrompt";
import {
  DAILY_LIMIT,
  incrementUsage,
  remainingToday,
  isLimitReached,
} from "../lib/usageLimit";

const GENRES = [
  "Dance Pop",
  "Chillout",
  "Boom Bap",
  "Cinematic Trap",
  "Deep House",
  "Ambient",
  "Kinderlied",
];

const MOODS = [
  "emotional",
  "fröhlich",
  "melancholisch",
  "episch",
  "düster",
  "motivierend",
];

export const Generator = ({ premium = false }) => {
  const [genre, setGenre] = useState("Dance Pop");
  const [mood, setMood] = useState("motivierend");
  const [bpm, setBpm] = useState(120);
  const [language, setLanguage] = useState("Deutsch");
  const [instrumental, setInstrumental] = useState(false);
  const [extras, setExtras] = useState("");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [remaining, setRemaining] = useState(() => remainingToday());
  const [limitReached, setLimitReached] = useState(() => isLimitReached());

  // Re-sync when premium status changes (e.g. activated via URL)
  useEffect(() => {
    setRemaining(remainingToday());
    setLimitReached(isLimitReached());
  }, [premium]);

  const goToPricing = () => {
    const el = document.getElementById("pricing");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleGenerate = () => {
    if (isLimitReached()) {
      setLimitReached(true);
      setRemaining(0);
      toast.error("Tageslimit erreicht", {
        description: `Im Free-Plan kannst du ${DAILY_LIMIT} Prompts pro Tag generieren. Upgrade für unbegrenzte Generierungen.`,
        action: {
          label: "Upgrade",
          onClick: goToPricing,
        },
      });
      return;
    }

    setLoading(true);
    setCopied(false);
    // Simulate a tiny delay so the UX feels "AI" — purely template-based
    setTimeout(() => {
      const prompt = generateSunoPrompt({
        genre,
        mood,
        bpm,
        language,
        instrumental,
        extras,
      });
      setResult(prompt);
      const next = incrementUsage();
      const left = premium ? Infinity : Math.max(0, DAILY_LIMIT - next.count);
      setRemaining(left);
      setLimitReached(!premium && left <= 0);
      setLoading(false);
      toast.success("Prompt erfolgreich generiert", {
        description: premium
          ? "Premium aktiv — generiere so oft du willst."
          : left > 0
            ? `Noch ${left} von ${DAILY_LIMIT} Generierungen heute übrig.`
            : "Letzte Generierung für heute verbraucht.",
      });
    }, 700);
  };

  const handleCopy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result);
      setCopied(true);
      toast.success("In Zwischenablage kopiert");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Konnte nicht kopieren");
    }
  };

  const handleDownload = () => {
    if (!result) return;
    const blob = new Blob([result], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `suno-prompt-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Download gestartet");
  };

  return (
    <section
      id="generator"
      data-testid="generator-section"
      className="relative py-24 px-6 border-t border-white/5"
    >
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <span className="text-xs uppercase tracking-[0.2em] text-[#CCFF00] font-semibold">
            Der Generator
          </span>
          <h2 className="font-display mt-3 text-4xl sm:text-5xl md:text-6xl font-black tracking-tighter text-white">
            In drei Klicks zum perfekten Prompt
          </h2>
          <p className="mt-4 text-zinc-400 max-w-xl mx-auto">
            Wähle Genre, Stimmung und Tempo — der Rest übernimmt PromptKiste.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Form */}
          <div
            data-testid="form-card"
            className="lg:col-span-7 glass rounded-3xl p-6 sm:p-8 relative overflow-hidden"
          >
            <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#CCFF00]/10 blur-3xl pointer-events-none" />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 relative">
              {/* Genre */}
              <div className="flex flex-col gap-2">
                <Label className="text-xs uppercase tracking-wider text-zinc-400 font-medium">
                  Musikstil
                </Label>
                <Select value={genre} onValueChange={setGenre}>
                  <SelectTrigger
                    data-testid="genre-select"
                    className="bg-white/5 border-white/10 text-white h-12 rounded-xl hover:bg-white/[0.07] focus:ring-2 focus:ring-[#CCFF00]/50 focus:border-[#CCFF00]/40"
                  >
                    <SelectValue placeholder="Wähle ein Genre" />
                  </SelectTrigger>
                  <SelectContent
                    data-testid="genre-options"
                    className="bg-[#0F1014] border-white/10 text-white"
                  >
                    {GENRES.map((g) => (
                      <SelectItem
                        key={g}
                        value={g}
                        data-testid={`genre-option-${g.toLowerCase().replace(/\s+/g, "-")}`}
                        className="focus:bg-[#CCFF00]/10 focus:text-white"
                      >
                        {g}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Mood */}
              <div className="flex flex-col gap-2">
                <Label className="text-xs uppercase tracking-wider text-zinc-400 font-medium">
                  Stimmung
                </Label>
                <Select value={mood} onValueChange={setMood}>
                  <SelectTrigger
                    data-testid="mood-select"
                    className="bg-white/5 border-white/10 text-white h-12 rounded-xl hover:bg-white/[0.07] focus:ring-2 focus:ring-[#CCFF00]/50 focus:border-[#CCFF00]/40"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F1014] border-white/10 text-white">
                    {MOODS.map((m) => (
                      <SelectItem
                        key={m}
                        value={m}
                        data-testid={`mood-option-${m}`}
                        className="focus:bg-[#CCFF00]/10 focus:text-white capitalize"
                      >
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* BPM */}
              <div className="flex flex-col gap-2">
                <Label className="text-xs uppercase tracking-wider text-zinc-400 font-medium">
                  BPM
                </Label>
                <Input
                  data-testid="bpm-input"
                  type="number"
                  min={40}
                  max={220}
                  value={bpm}
                  onChange={(e) => setBpm(e.target.value)}
                  className="bg-white/5 border-white/10 text-white h-12 rounded-xl placeholder:text-zinc-600 focus-visible:ring-2 focus-visible:ring-[#CCFF00]/50 focus-visible:border-[#CCFF00]/40"
                  placeholder="z. B. 124"
                />
              </div>

              {/* Language */}
              <div className="flex flex-col gap-2">
                <Label className="text-xs uppercase tracking-wider text-zinc-400 font-medium">
                  Sprache
                </Label>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger
                    data-testid="language-select"
                    className="bg-white/5 border-white/10 text-white h-12 rounded-xl hover:bg-white/[0.07] focus:ring-2 focus:ring-[#CCFF00]/50 focus:border-[#CCFF00]/40"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F1014] border-white/10 text-white">
                    <SelectItem
                      value="Deutsch"
                      data-testid="language-option-de"
                      className="focus:bg-[#CCFF00]/10"
                    >
                      Deutsch
                    </SelectItem>
                    <SelectItem
                      value="Englisch"
                      data-testid="language-option-en"
                      className="focus:bg-[#CCFF00]/10"
                    >
                      Englisch
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Instrumental checkbox */}
            <div className="mt-6 flex items-center gap-3 p-4 rounded-xl bg-white/[0.03] border border-white/5">
              <Checkbox
                id="instrumental"
                data-testid="instrumental-checkbox"
                checked={instrumental}
                onCheckedChange={(v) => setInstrumental(Boolean(v))}
                className="border-white/30 data-[state=checked]:bg-[#CCFF00] data-[state=checked]:border-[#CCFF00] data-[state=checked]:text-black"
              />
              <Label
                htmlFor="instrumental"
                className="text-sm text-zinc-300 cursor-pointer select-none"
              >
                Instrumental ohne Gesang
              </Label>
            </div>

            {/* Extras */}
            <div className="mt-5 flex flex-col gap-2">
              <Label className="text-xs uppercase tracking-wider text-zinc-400 font-medium">
                Zusatzwünsche
              </Label>
              <Textarea
                data-testid="extras-textarea"
                value={extras}
                onChange={(e) => setExtras(e.target.value)}
                placeholder="z. B. weibliche Stimme, Saxophon-Solo in der Bridge, 80s Synthwave Vibe …"
                className="bg-white/5 border-white/10 text-white rounded-xl min-h-[110px] resize-none placeholder:text-zinc-600 focus-visible:ring-2 focus-visible:ring-[#CCFF00]/50 focus-visible:border-[#CCFF00]/40"
              />
            </div>

            {/* Generate */}
            <Button
              data-testid="generate-btn"
              onClick={handleGenerate}
              disabled={loading || limitReached}
              className="mt-6 w-full bg-[#CCFF00] hover:bg-[#B3E600] disabled:bg-white/10 disabled:text-zinc-500 disabled:cursor-not-allowed text-black font-bold h-14 rounded-2xl text-base transition-all hover:shadow-[0_0_32px_-4px_rgba(204,255,0,0.65)]"
            >
              {loading ? (
                <>
                  <Wand2 className="w-5 h-5 mr-2 animate-pulse" />
                  Generiere …
                </>
              ) : limitReached ? (
                <>
                  <Lock className="w-5 h-5 mr-2" strokeWidth={2.5} />
                  Tageslimit erreicht
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" strokeWidth={2.5} />
                  Prompt generieren
                </>
              )}
            </Button>

            {/* Usage counter / premium badge */}
            {premium ? (
              <div
                data-testid="premium-active-banner"
                className="mt-4 rounded-xl border border-[#CCFF00]/30 bg-[#CCFF00]/[0.06] p-4 flex items-center gap-3"
              >
                <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-[#CCFF00] flex items-center justify-center">
                  <Crown className="w-4.5 h-4.5 text-black" strokeWidth={2.5} />
                </span>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-white">
                    Premium aktiv
                  </p>
                  <p className="text-xs text-zinc-400 mt-0.5">
                    Unbegrenzte Generierungen freigeschaltet — viel Spaß beim Erstellen.
                  </p>
                </div>
                <span
                  data-testid="premium-pill"
                  className="hidden sm:inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-bold text-black bg-[#CCFF00] px-2 py-1 rounded-full"
                >
                  Pro
                </span>
              </div>
            ) : (
              <>
                <div
                  data-testid="usage-info"
                  className="mt-4 flex flex-wrap items-center justify-between gap-3 text-xs"
                >
                  <span className="text-zinc-500">
                    Free-Plan:{" "}
                    <span
                      data-testid="usage-remaining"
                      className={
                        limitReached
                          ? "text-[#FF007A] font-semibold"
                          : "text-zinc-300 font-semibold"
                      }
                    >
                      {remaining}/{DAILY_LIMIT}
                    </span>{" "}
                    Generierungen heute übrig
                  </span>
                  <button
                    data-testid="usage-upgrade-link"
                    onClick={goToPricing}
                    className="inline-flex items-center gap-1.5 text-[#CCFF00] hover:underline font-medium"
                  >
                    <Zap className="w-3.5 h-3.5" />
                    Unbegrenzt mit Pro
                  </button>
                </div>

                {limitReached && (
                  <div
                    data-testid="limit-banner"
                    className="mt-4 rounded-xl border border-[#FF007A]/30 bg-[#FF007A]/[0.06] p-4 flex flex-col sm:flex-row sm:items-center gap-3"
                  >
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-white">
                        Tageslimit erreicht
                      </p>
                      <p className="text-xs text-zinc-400 mt-0.5">
                        Komm morgen wieder oder schalte mit Pro unbegrenzte Generierungen frei.
                      </p>
                    </div>
                    <Button
                      data-testid="limit-banner-cta"
                      onClick={goToPricing}
                      className="bg-[#CCFF00] hover:bg-[#B3E600] text-black font-bold rounded-lg h-10 px-4"
                    >
                      Pro freischalten
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Result */}
          <div
            data-testid="result-card"
            className="lg:col-span-5 rounded-3xl bg-[#0A0B0E] border border-white/5 p-6 sm:p-8 flex flex-col relative overflow-hidden"
          >
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#CCFF00] to-transparent opacity-60" />

            <div className="flex items-center justify-between mb-5">
              <div>
                <span className="text-xs uppercase tracking-[0.18em] text-zinc-500 font-semibold">
                  Dein Prompt
                </span>
                <h3 className="font-display text-2xl font-bold text-white mt-0.5">
                  Ergebnis
                </h3>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-yellow-400/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#CCFF00]/80" />
              </div>
            </div>

            <div
              data-testid="result-output"
              className="font-mono text-[13px] leading-relaxed text-zinc-200 bg-black/40 border border-white/5 rounded-xl p-5 flex-1 min-h-[260px] whitespace-pre-wrap overflow-auto"
            >
              {result ? (
                result
              ) : (
                <span className="text-zinc-600">
                  {"// Dein generierter Suno-Prompt erscheint hier.\n// Wähle links deine Optionen und klicke auf »Prompt generieren«."}
                </span>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mt-5">
              <Button
                data-testid="copy-btn"
                onClick={handleCopy}
                disabled={!result}
                variant="outline"
                className="flex-1 bg-white/5 border-white/10 hover:bg-white/10 text-white rounded-xl h-12 disabled:opacity-40"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 mr-2 text-[#CCFF00]" />
                    Kopiert
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    Ergebnis kopieren
                  </>
                )}
              </Button>
              <Button
                data-testid="download-btn"
                onClick={handleDownload}
                disabled={!result}
                variant="outline"
                className="flex-1 bg-white/5 border-white/10 hover:bg-white/10 text-white rounded-xl h-12 disabled:opacity-40"
              >
                <Download className="w-4 h-4 mr-2" />
                Als TXT
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
